import {getNetworkSocket} from '../../utils/networkSocket';
import {GAME_TYPES} from '../../constants/game';
import {
  createPhaseActionGuard,
  PHASE_ACTION_GUARD_FIELDS,
  TURN_CONTEXT_FREE_ACTION_TYPES,
} from '../../utils/gameState/turn/phaseActionGuard';

const BLACKLISTED_TYPES = [
  'SET_PLAYER_AUTH',
  'CLEAR_PLAYER_AUTH',
  'SET_LOBBY',
  'LOBBY_UPDATE',
  'LEAVE_LOBBY',
  'LOAD_STATE',
  'SHOW_TOAST',
  'HIDE_TOAST',
];

/**
 * В уходящую на сервер копию экшена подмешивается слепок контекста хода
 * {round, currentPlayerId, phase, warPhaseStage} из состояния ДО применения
 * экшена. По нему сервер (RELAY_ACTION, shouldRejectStaleRelayedAction)
 * отбрасывает действия, отправленные из уже неактуального контекста, —
 * например стройку, нажатую в момент, когда ход игрока уже завершен серверным
 * таймером, но экшен завершения еще не долетел до клиента.
 *
 * Слепок не подмешивается:
 *  - до старта партии (в лобби контекста хода нет);
 *  - в экшены вне контекста хода (TURN_CONTEXT_FREE_ACTION_TYPES — чат матча):
 *    их законно совершают в любой момент, в том числе в чужой ход;
 *  - в экшены, уже несущие поля слепка (завершения ходов — их слепок создан
 *    в месте диспатча и авторитетен);
 *  - в экшены без payload-объекта: слепок некуда класть, и сервер такой экшен
 *    не отбросит никогда. Поэтому у игровых экшенов без данных payload —
 *    пустой объект (`payload: {}`); без него, например, отмена снабжения,
 *    долетевшая после завершения хода по таймеру, применялась к следующему
 *    игроку. Исключение (networkMiddleware.test.js) — CLEAR_HEX с ключом
 *    гекса строкой.
 *
 * Локально диспатчится оригинальный экшен без слепка.
 *
 * Экспортируется для e2e-харнессов (server/scripts): они шлют RELAY_ACTION
 * ровно так же, как настоящий клиент.
 */
export const buildGuardedRelayAction = (action, state) => {
  const gameState = state.shared?.gameState;
  if (!gameState?.isStarted || TURN_CONTEXT_FREE_ACTION_TYPES.includes(action.type)) {
    return action;
  }
  const payload = action.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return action;
  }
  if (PHASE_ACTION_GUARD_FIELDS.some((field) => field in payload)) {
    return action;
  }
  return {...action, payload: {...payload, ...createPhaseActionGuard(gameState)}};
};

const networkMiddleware = (store) => (next) => (action) => {
  const state = store.getState();
  const lobbyId = state.private?.lobby?.lobbyId;
  // Просмотр записи матча — чисто локальное воспроизведение: экшены записи
  // ни в коем случае не уходят на сервер. Реле здесь включается по наличию
  // lobbyId, а он может остаться от прошлой сетевой партии, так что без
  // этой проверки все 700+ экшенов записи улетели бы в RELAY_ACTION
  const isReplay = state.shared?.gameState?.gameType === GAME_TYPES.REPLAY;

  if (!isReplay && lobbyId && action && !action.noServerSync && !BLACKLISTED_TYPES.includes(action.type) && !action._fromServer) {
    const ws = getNetworkSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: 'RELAY_ACTION',
          payload: {lobbyId, action: buildGuardedRelayAction(action, state)},
        })
      );

      // console.log('TO SERVER');
      // console.log({
      //   type: 'RELAY_ACTION',
      //   payload: {lobbyId, action},
      // })
    }
  }

  return next(action);
};

export default networkMiddleware;
